import React, { useState } from 'react';
import { X, ShieldCheck, Loader2 } from 'lucide-react';
import { DbsCheckType, Driver } from '../../services/api';

interface RequestDbsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (checkType: DbsCheckType) => Promise<void>;
    driver: Driver;
}

const CHECK_TYPES: { type: DbsCheckType; label: string; description: string }[] = [
    {
        type: 'basic',
        label: 'Basic',
        description: 'Unspent convictions only. Suitable for most transport and driving roles.',
    },
    {
        type: 'standard',
        label: 'Standard',
        description: 'Spent and unspent convictions, cautions, reprimands and warnings.',
    },
    {
        type: 'enhanced',
        label: 'Enhanced',
        description: 'Standard check plus any relevant information held by local police. For roles with regulated activity.',
    },
];

const RequestDbsModal: React.FC<RequestDbsModalProps> = ({ isOpen, onClose, onConfirm, driver }) => {
    const [selected, setSelected] = useState<DbsCheckType>('basic');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        try {
            await onConfirm(selected);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                            <ShieldCheck className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-900">Request DBS Check</h3>
                            <p className="text-xs text-gray-500">{driver.firstName} {driver.lastName}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Select the check type. An identity verification link will be emailed to the driver at <span className="font-medium text-gray-900">{driver.email}</span>.
                    </p>

                    <div className="space-y-2">
                        {CHECK_TYPES.map(({ type, label, description }) => {
                            const isSelected = selected === type;
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => setSelected(type)}
                                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                                        isSelected
                                            ? 'border-green-400 bg-green-50'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                    style={isSelected ? { borderColor: '#69BE28' } : {}}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                            isSelected ? 'border-green-500' : 'border-gray-300'
                                        }`} style={isSelected ? { borderColor: '#69BE28' } : {}}>
                                            {isSelected && (
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#69BE28' }} />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{label}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-xs text-amber-800">
                            The link expires after 14 days if unused. A new check cannot be requested while one is in progress.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isSubmitting ? 'Sending...' : 'Send DBS Invite'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RequestDbsModal;
